import PropTypes from 'prop-types'
import { useRef, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Button from 'components/Button'
import useContent from 'store/content'
import { testRiddleAnswer } from 'libs/localApi'

import {
  createCookieWithTimeParams,
  validateRiddleCookie,
  addBrToText,
  isLinkInternal,
  getRouterLink,
  setCurrentDomainToLink,
} from 'utils'

import {
  StyledSmallLabel,
  StyledForm,
  StyledInput,
  StyledLabel,
  StyledLoader,
  RiddleImageBefore,
  RiddleImageAfter,
  RiddleThumbnailImage,
  StyledTitle
} from './styles'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import PwaOfflinePlaceholder from 'components/PwaOfflinePlaceholder'
import { withUtm } from 'utils/utm'

const KNOWN_VALUE = ' * * * *'

const Riddle = ({ part, themeInfos }) => {
  const [valid, setValid] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const content = useContent()
  const router = useRouter()
  const inputEl = useRef(null)

  const appId = content.get.appId()
  const riddleInfos = content.get.riddleById(part.id)
  const online = useOnlineStatus()

  useEffect(() => {
    const hasCookie = validateRiddleCookie(riddleInfos)
    setValid(hasCookie)
    setSubmitted(hasCookie)
  }, [])

  if (!online) {
    return <PwaOfflinePlaceholder />
  }

  if (!riddleInfos) {
    return null
  }

  const getIsInvalid = () => {
    const { onError } = riddleInfos

    if (onError) {
      const isInvalid = submitted && !valid
      const hasMessage = onError.action === 'message' && onError.message.length > 0

      return isInvalid && hasMessage
    }

    return false
  }

  const redirect = (url) => {
    setLoading(true)
    setTimeout(() => {
      if (isLinkInternal(url)) {
        const { href, hardNavigation } = getRouterLink(url, router.query.appId, router.asPath)
        if (hardNavigation) {
          window.location.href = withUtm(href)
        } else {
          router.push(withUtm(href))
        }
      } else {
        window.location.href = withUtm(setCurrentDomainToLink(url))
      }
      setLoading(false)
    }, 750)
  }

  const handleOnSubmit = async (e) => {
    const { id, onSuccess, onError, unlockTime } = riddleInfos

    // needs preventDefault to redirect
    e.preventDefault()

    // if COOKIE GO!
    const pass = inputEl.current.value

    const isRight = pass !== '' ? await testRiddleAnswer(appId, id, pass) : valid && submitted

    setValid(isRight)
    setSubmitted(true)

    if (isRight) {
      createCookieWithTimeParams(`riddle-${id}`, unlockTime.value, unlockTime.unit)
      redirect(onSuccess.url)
    } else if (onError.action === 'redirect') {
      redirect(onError.url)
    }

    return false
  }

  const {
    onError: { message = '' } = {},
    display: {
      title = '',
      text = '',
      button = '',
      headerBanner = '',
      thumbnailImage = '',
      footerBanner = '',
      color = '',
    } = {},
    style: { borderRadius = '0' } = {}
  } = riddleInfos

  return (
    <>
      {headerBanner !== '' && (
        <RiddleImageBefore>
          <img src={headerBanner} alt="riddle" />
        </RiddleImageBefore>
      )}

      <StyledForm autoComplete="off" onSubmit={handleOnSubmit} $color={color}>
        {thumbnailImage !== '' && (
          <RiddleThumbnailImage $borderRadius={borderRadius}>
            <div>
              <img src={thumbnailImage} alt={title} />
            </div>
          </RiddleThumbnailImage>
        )}

        {title && title.length > 0 && <StyledTitle>{title}</StyledTitle>}

        {text && text.length > 0 && (
          <StyledLabel htmlFor={`lockpass-${part.id}`} style={{ marginBottom: '20px' }}>
            {addBrToText(text, 'lock-txt')}
          </StyledLabel>
        )}

        <StyledInput
          ref={inputEl}
          name={`lockpass-${part.id}`}
          id={`lockpass-${part.id}`}
          placeholder={submitted && valid ? KNOWN_VALUE : ''}
          $error={submitted && !valid}
          $valid={submitted && valid}
        />

        {getIsInvalid() && <StyledSmallLabel $state="error">{message}</StyledSmallLabel>}

        <Button id="lock submit button" onClick={handleOnSubmit} disabled={loading} isLock themeInfos={themeInfos}>
          <>
            {loading && <StyledLoader />}
            {button}
          </>
        </Button>
      </StyledForm>

      {footerBanner !== '' && (
        <RiddleImageAfter>
          <img src={footerBanner} alt="riddle" />
        </RiddleImageAfter>
      )}
    </>
  )
}

Riddle.propTypes = {
  part: PropTypes.object,
  t: PropTypes.func,
}

export default Riddle
